/**
 * AI Chat Protocol Types
 *
 * Shared between frontend (apps/web) and backend (apps/api).
 *
 * This module defines the complete typed message protocol for the AI chat
 * WebSocket session. The protocol is deliberately transport-agnostic - the
 * types describe the message contract, not the WebSocket framing.
 *
 * See context/ai-chat.md for the full architecture reference.
 */
/** Map an explicit run agentKey (button flows) to its trigger source. */
export function triggerSourceForAgentKey(agentKey) {
    switch (agentKey) {
        case 'sales_company_enrichment':
            return 'button_enrich';
        case 'deal_transcript_analysis':
            return 'button_transcript';
        case 'sales_deal_generation':
        case 'regenerate_estimate':
        case 'regenerate_sow':
            return 'button_generate';
        case undefined:
            return 'user_chat';
        default:
            return 'button_action';
    }
}
/**
 * Resolve chosen option id(s) to a readable, comma-joined label string. Handles
 * both multi-select (`choiceIds`) and single-select (`choiceId`); an unknown id
 * falls back to the raw id so nothing is silently dropped.
 */
function joinChoiceLabels(choices, choiceIds, choiceId) {
    const ids = choiceIds?.length ? choiceIds : choiceId ? [choiceId] : [];
    if (ids.length === 0)
        return '';
    return ids.map((id) => choices?.find((c) => c.id === id)?.label ?? id).join(', ');
}
/**
 * Resolve a {@link UserPromptResponse} to the selected options that carry a
 * `preview`, so the answered transcript can render them as collapsed disclosures.
 * Pure and shared (client reducer + server transcript builder) so live and
 * rehydrated history stay identical. Returns one entry per selected option that
 * has a non-empty preview; an empty array when none do.
 */
export function deriveSelectedPreviews(req, response) {
    const out = [];
    const collect = (choices, ids) => {
        for (const id of ids) {
            const c = choices?.find((cc) => cc.id === id);
            if (c?.preview?.trim())
                out.push({ label: c.label, preview: c.preview });
        }
    };
    if (response.kind === 'choice') {
        const ids = response.choiceIds?.length
            ? response.choiceIds
            : response.choiceId
                ? [response.choiceId]
                : [];
        collect(req?.choices, ids);
    }
    else if (response.kind === 'form') {
        for (const a of response.answers ?? []) {
            if (a.kind !== 'choice')
                continue;
            const q = req?.questions?.find((qq) => qq.id === a.questionId);
            const ids = a.choiceIds?.length ? a.choiceIds : a.choiceId ? [a.choiceId] : [];
            collect(q?.choices, ids);
        }
    }
    return out;
}
/**
 * Resolve a raw {@link UserPromptResponse} into the human-readable answer fields a
 * prompt segment carries. Pure and shared so the live client reducer and the
 * server-side transcript builder produce identical answers (no drift).
 */
export function derivePromptAnswer(req, response) {
    // Form (multi-question) prompts: resolve each tab's choice to a readable
    // {section header → chosen option label} row.
    let formAnswers;
    if (response.kind === 'form') {
        const questions = req?.questions ?? [];
        formAnswers = (response.answers ?? []).map((a) => {
            const q = questions.find((qq) => qq.id === a.questionId);
            const value = a.kind === 'confirm'
                ? a.confirmed
                    ? 'Yes'
                    : 'No'
                : a.kind === 'choice'
                    ? joinChoiceLabels(q?.choices, a.choiceIds, a.choiceId)
                    : a.kind === 'text'
                        ? // An empty text answer only arrives via "Skip" (Send is disabled when
                            // empty), so show it as Skipped rather than a blank row.
                            a.text?.trim()
                                ? a.text
                                : 'Skipped'
                        : '';
            return { questionId: a.questionId, label: q?.header ?? q?.prompt ?? a.questionId, value };
        });
    }
    const answer = response.kind === 'confirm'
        ? response.confirmed
            ? 'Yes'
            : 'No'
        : response.kind === 'choice'
            ? joinChoiceLabels(req?.choices, response.choiceIds, response.choiceId)
            : response.kind === 'file'
                ? 'Uploaded file'
                : response.kind === 'form'
                    ? `${formAnswers?.length ?? 0} section${(formAnswers?.length ?? 0) === 1 ? '' : 's'} confirmed`
                    : // Empty single-text answer ⇒ the optional question was skipped.
                        response.text?.trim()
                            ? response.text
                            : 'Skipped';
    return {
        answer,
        ...(formAnswers ? { formAnswers } : {}),
        ...(response.kind === 'file' && response.assetUuid
            ? { answerAssetUuid: response.assetUuid, answerAssetUuids: response.assetUuids }
            : {}),
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Helper type guards
// ─────────────────────────────────────────────────────────────────────────────
export function isServerEvent(raw) {
    return (typeof raw === 'object' &&
        raw !== null &&
        'type' in raw &&
        typeof raw['type'] === 'string');
}
export function isClientCommand(raw) {
    return (typeof raw === 'object' &&
        raw !== null &&
        'type' in raw &&
        typeof raw['type'] === 'string');
}
//# sourceMappingURL=ai-chat.js.map